import * as React from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import PeopleRoundedIcon from '@mui/icons-material/PeopleRounded';
import DirectionsCar from '@mui/icons-material/DirectionsCar';
import EvStation from '@mui/icons-material/EvStation';

import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import StarBorder from '@mui/icons-material/StarBorder';
import { useNavigate, useLocation } from 'react-router-dom';


// === CONFIGURATION: Define your entire menu here ===
const menuConfig = [
  {
    text: 'Building Dashboard',
    icon: <HomeRoundedIcon />,
    subMenu: [
      { text: 'Overview', toLink: '/' },
      { text: 'Reports', toLink: '/building/reports' },
      { text: 'Fleet', toLink: '/building/fleet' },
    ],
  },
  {
    text: 'Vehicle Dashboard', icon: <DirectionsCar />, toLink: '/vehicle-dashboard'
    , subMenu: [
      { text: 'Overview', toLink: '/vehicle-dashboard' },
      { text: 'Reports', toLink: '/vehicle-dashboard/reports' },
      { text: 'Fleet', toLink: '/vehicle-dashboard/fleet' },
    ],

  },
  {
    text: 'Charger Dashboard', icon: <EvStation />, toLink: '/charger-dashboard'
    , subMenu: [
      { text: 'Overview', toLink: '/charger-dashboard' },
      { text: 'Reports', toLink: '/charger-dashboard/reports' },
      { text: 'Fleet', toLink: '/charger-dashboard/fleet' },
    ],
  },
  { text: 'Company Settings', icon: <SettingsRoundedIcon />, toLink: '/config' },
];

// === COMPONENT ===
export default function MenuContent({ collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [openSubmenus, setOpenSubmenus] = React.useState({});

  const isItemOrChildSelected = (item, path) => {
    if (item.toLink === path) return true;
    if (item.subMenu) {
      return item.subMenu.some((subItem) => isItemOrChildSelected(subItem, path));
    }
    return false;
  };

  React.useEffect(() => {
    const openParents = {};

    const findAndOpenParents = (items, path) => {
      for (const item of items) {
        if (item.subMenu) {
          const childMatch = findAndOpenParents(item.subMenu, path);
          if (childMatch) {
            openParents[item.text] = true;
            return true;
          }
        }
        if (item.toLink === path) {
          return true;
        }
      }
      return false;
    };

    findAndOpenParents(menuConfig, location.pathname);
    setOpenSubmenus(openParents);
  }, [location.pathname]);

  const handleToggle = (menuText) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [menuText]: !prev[menuText],
    }));
  };


  const renderMenuItems = (items, level = 0) => {
    return items.map((item) => {
      const hasSubMenu = !!item.subMenu && item.subMenu.length > 0;
      const isOpen = openSubmenus[item.text] || false;

      const paddingLeft = 2 + level * 2;
      const isSelected = location.pathname === item.toLink;
      const isSubItemSelected = isItemOrChildSelected(item, location.pathname);

      if (hasSubMenu) {
        return (
          <React.Fragment key={item.text}>
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                onClick={() => handleToggle(item.text)}
                sx={{
                  pl: paddingLeft,
                  bgcolor: isSubItemSelected ? '#e3f2fd' : 'inherit',
                  '&:hover': {
                    bgcolor: isSubItemSelected ? '#bbdefb' : 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  {item.icon || <StarBorder />}
                </ListItemIcon>
                <ListItemText primary={!collapsed && item.text} />
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {renderMenuItems(item.subMenu, level + 1)}
              </List>
            </Collapse>
          </React.Fragment>
        );
      }

      return (
        <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            sx={{
              pl: paddingLeft,
              bgcolor: isSelected ? '#e3f2fd' : 'inherit',
              '&:hover': {
                bgcolor: isSelected ? '#bbdefb' : 'action.hover',
              },
            }}
            selected={isSelected}
            onClick={() => item.toLink && navigate(item.toLink)}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>
              {item.icon || <StarBorder />}
            </ListItemIcon>
            <ListItemText primary={!collapsed && item.text} />
          </ListItemButton>
        </ListItem>
      );
    });
  };

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>{renderMenuItems(menuConfig)}</List>
    </Stack>
  );
}
